import { useAuth } from "./useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { isOnline, queueUserPreference } from "@/lib/offlineSync";

const KG_TO_LBS = 2.20462;
const LBS_TO_KG = 0.453592;
const KM_TO_MI = 0.621371;
const MI_TO_KM = 1.60934;

export const MUSIC_GENRES = [
  { value: "hip-hop", label: "Hip-Hop" },
  { value: "pop", label: "Pop" },
  { value: "kpop", label: "K-Pop" },
  { value: "rock", label: "Rock" },
  { value: "country", label: "Country" },
  { value: "edm", label: "EDM" },
  { value: "latin", label: "Latin" },
] as const;

export const AVATAR_OPTIONS = [
  { id: "dumbbell", label: "Dumbbell" },
  { id: "barbell", label: "Barbell" },
  { id: "kettlebell", label: "Kettlebell" },
  { id: "running", label: "Running" },
  { id: "yoga", label: "Yoga" },
  { id: "heart", label: "Heart" },
  { id: "lightning", label: "Lightning" },
  { id: "fire", label: "Fire" },
  { id: "star", label: "Star" },
  { id: "trophy", label: "Trophy" },
] as const;

export const EXPERIENCE_LEVELS = [
  { id: "beginner", label: "Beginner", description: "0-1 years of training" },
  { id: "intermediate", label: "Intermediate", description: "1-3 years of training" },
  { id: "advanced", label: "Advanced", description: "3+ years of training" },
] as const;

export const BARBELL_PRESETS = [
  { id: "olympic", label: "Men's Olympic Bar", weightKg: 20, weightLbs: 44.1 },
  { id: "womens", label: "Women's Olympic Bar", weightKg: 15, weightLbs: 33.1 },
  { id: "custom", label: "Custom", weightKg: null, weightLbs: null },
] as const;

// Helper to get label with user's weight unit
export function getBarbellPresetLabel(presetId: string, weightUnit: "kg" | "lbs"): string {
  const preset = BARBELL_PRESETS.find(p => p.id === presetId);
  if (!preset || preset.id === "custom") return "Custom";
  const weight = weightUnit === "lbs" ? preset.weightLbs : preset.weightKg;
  return `${preset.label} (${weight}${weightUnit})`;
}

export const BARBELL_ENTRY_MODES = [
  { id: "total", label: "Total weight", description: "Bar + all plates" },
  { id: "total_added", label: "Weight added", description: "All plates only" },
  { id: "per_side", label: "Per side", description: "Plates on one side" },
] as const;

export const PROGRESSION_FREQUENCIES = [
  { id: "every_session", label: "Every 1 Session" },
  { id: "every_2_sessions", label: "Every 2 Sessions" },
  { id: "every_3_sessions", label: "Every 3 Sessions" },
  { id: "weekly", label: "Weekly" },
  { id: "every_2_weeks", label: "Every 2 Weeks" },
  { id: "off", label: "Off" },
] as const;

export const MAX_DUMBBELL_MODES = [
  { id: "global", label: "Same for all programs", description: "One limit applies everywhere" },
  { id: "per_program", label: "Set per program", description: "Different limits for each program" },
] as const;

export type MusicGenre = typeof MUSIC_GENRES[number]["value"];
export type AvatarId = typeof AVATAR_OPTIONS[number]["id"];
export type ExperienceLevel = typeof EXPERIENCE_LEVELS[number]["id"];
export type BarbellEntryMode = typeof BARBELL_ENTRY_MODES[number]["id"];
export type ProgressionFrequency = typeof PROGRESSION_FREQUENCIES[number]["id"];
export type MaxDumbbellMode = typeof MAX_DUMBBELL_MODES[number]["id"];
export type ProgressionMode = "double" | "strength";

export type DistanceUnit = "km" | "mi";

// Helper to get cached preference with fallback
function getCachedPreference<T>(key: string, defaultValue: T): T {
  try {
    const cached = localStorage.getItem('myFitBeats_cachedUser');
    if (cached) {
      const user = JSON.parse(cached);
      if (user && user[key] !== undefined && user[key] !== null) {
        return user[key] as T;
      }
    }
  } catch {}
  return defaultValue;
}

export function usePreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Use cached preferences as fallback to prevent "flash" to defaults during load/refetch
  const weightUnit = (user as any)?.weightUnit || getCachedPreference('weightUnit', "kg");
  const distanceUnit: DistanceUnit = (user as any)?.distanceUnit || getCachedPreference<DistanceUnit>('distanceUnit', "km");
  const musicGenre = (user as any)?.musicGenre || getCachedPreference('musicGenre', "hip-hop");
  const avatarId = (user as any)?.avatarId || getCachedPreference('avatarId', "dumbbell");
  const experienceLevel = (user as any)?.experienceLevel as ExperienceLevel | null;
  const barbellWeightKg: number = (user as any)?.barbellWeight ?? getCachedPreference('barbellWeight', 20.4117);
  const barbellEntryMode: BarbellEntryMode = (user as any)?.barbellEntryMode || getCachedPreference<BarbellEntryMode>('barbellEntryMode', "total");
  // Increments are stored in kg - will be converted after displayWeight is defined
  const barbellIncrementKg: number | null = (user as any)?.barbellIncrement ?? getCachedPreference<number | null>('barbellIncrement', null);
  const dumbbellIncrementKg: number | null = (user as any)?.dumbbellIncrement ?? getCachedPreference<number | null>('dumbbellIncrement', null);
  const maxDumbbellWeightKg: number | null = (user as any)?.maxDumbbellWeight ?? getCachedPreference<number | null>('maxDumbbellWeight', null);
  const maxDumbbellMode: MaxDumbbellMode = (user as any)?.maxDumbbellMode || getCachedPreference<MaxDumbbellMode>('maxDumbbellMode', "global");
  const progressionFrequency: ProgressionFrequency = (user as any)?.progressionFrequency || getCachedPreference<ProgressionFrequency>('progressionFrequency', "every_session");
  const progressionMode: ProgressionMode = (user as any)?.progressionMode || getCachedPreference<ProgressionMode>('progressionMode', "double");
  const rirEnabled: boolean = (user as any)?.rirEnabled ?? getCachedPreference<boolean>('rirEnabled', true);
  const rirProgressThreshold: number = (user as any)?.rirProgressThreshold ?? getCachedPreference<number>('rirProgressThreshold', 4);
  const rirDeloadThreshold: number = (user as any)?.rirDeloadThreshold ?? getCachedPreference<number>('rirDeloadThreshold', 1);
  const workoutReminderTime: string = (user as any)?.workoutReminderTime || getCachedPreference('workoutReminderTime', "08:00");

  const updatePreferences = useMutation({
    mutationFn: async (prefs: { weightUnit?: "kg" | "lbs"; distanceUnit?: DistanceUnit; musicGenre?: MusicGenre; avatarId?: AvatarId; experienceLevel?: ExperienceLevel; barbellWeight?: number; barbellEntryMode?: BarbellEntryMode; barbellIncrement?: number | null; dumbbellIncrement?: number | null; maxDumbbellWeight?: number | null; maxDumbbellMode?: MaxDumbbellMode; progressionFrequency?: ProgressionFrequency; progressionMode?: ProgressionMode; rirEnabled?: boolean; rirProgressThreshold?: number; rirDeloadThreshold?: number; workoutReminderTime?: string }) => {
      if (!isOnline()) {
        queueUserPreference(prefs);
        return prefs;
      }
      const response = await apiRequest("PATCH", "/api/user/preferences", prefs);
      return response.json();
    },
    onMutate: async (prefs) => {
      await queryClient.cancelQueries({ queryKey: ["/api/auth/user"] });
      const previousUser = queryClient.getQueryData(["/api/auth/user"]);
      queryClient.setQueryData(["/api/auth/user"], (old: any) => ({
        ...old,
        ...prefs,
      }));
      return { previousUser };
    },
    onError: (err, prefs, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(["/api/auth/user"], context.previousUser);
      }
    },
    onSuccess: (data) => {
      if (data && typeof data === 'object') {
        localStorage.setItem('myFitBeats_cachedUser', JSON.stringify(data));
        // Update cache with server response to ensure data consistency
        queryClient.setQueryData(["/api/auth/user"], (old: any) => ({
          ...old,
          ...data,
        }));
      }
    },
  });

  const clearUserData = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", "/api/user/data");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  // High-precision conversion for storage (3 decimals) - prevents rounding drift
  const convertWeightForStorage = (weight: number, fromUnit: "kg" | "lbs", toUnit: "kg" | "lbs"): number => {
    if (fromUnit === toUnit) return weight;
    if (fromUnit === "kg" && toUnit === "lbs") {
      return Math.round(weight * KG_TO_LBS * 1000) / 1000;
    }
    return Math.round(weight * LBS_TO_KG * 1000) / 1000;
  };

  // Display conversion (1 decimal) - for user-facing values only
  const convertWeight = (weight: number, fromUnit: "kg" | "lbs", toUnit: "kg" | "lbs"): number => {
    if (fromUnit === toUnit) return Math.round(weight * 10) / 10;
    if (fromUnit === "kg" && toUnit === "lbs") {
      return Math.round(weight * KG_TO_LBS * 10) / 10;
    }
    return Math.round(weight * LBS_TO_KG * 10) / 10;
  };
  
  // Higher precision conversion for increment values to avoid drift
  // Rounds to nearest 0.25 to match common gym plate increments (1.25, 2.5, 5, etc.)
  // This prevents floating point drift like 2.5 lbs -> kg -> 2.49 lbs
  const convertIncrementWeight = (weight: number, fromUnit: "kg" | "lbs", toUnit: "kg" | "lbs"): number => {
    if (fromUnit === toUnit) return weight;
    let converted: number;
    if (fromUnit === "kg" && toUnit === "lbs") {
      converted = weight * KG_TO_LBS;
    } else {
      converted = weight * LBS_TO_KG;
    }
    // Round to nearest 0.25 to match common plate increments
    return Math.round(converted * 4) / 4;
  };

  const displayWeight = (weight: number, storedUnit: "kg" | "lbs" = "kg"): number => {
    return convertWeight(weight, storedUnit, weightUnit);
  };

  const toStorageWeight = (weight: number, displayUnit: "kg" | "lbs" = weightUnit): number => {
    // Use high-precision for storage to prevent rounding drift
    return convertWeightForStorage(weight, displayUnit, "kg");
  };
  
  // Higher precision version for increment storage
  const toStorageIncrement = (weight: number, displayUnit: "kg" | "lbs" = weightUnit): number => {
    return convertIncrementWeight(weight, displayUnit, "kg");
  };

  const formatWeight = (weight: number, storedUnit: "kg" | "lbs" = "kg"): string => {
    const converted = displayWeight(weight, storedUnit);
    return `${converted} ${weightUnit}`;
  };

  const convertDistance = (distance: number, fromUnit: DistanceUnit, toUnit: DistanceUnit): number => {
    if (fromUnit === toUnit) return distance;
    if (fromUnit === "km" && toUnit === "mi") {
      // Use 2 decimal places for display
      return Math.round(distance * KM_TO_MI * 100) / 100;
    }
    // Use 6 decimal places for storage (mi to km) to preserve precision
    // This prevents pace calculation errors from rounding
    return Math.round(distance * MI_TO_KM * 1000000) / 1000000;
  };

  const displayDistance = (distance: number, storedUnit: DistanceUnit = "km"): number => {
    return convertDistance(distance, storedUnit, distanceUnit);
  };

  const toStorageDistance = (distance: number, displayUnitParam: DistanceUnit = distanceUnit): number => {
    return convertDistance(distance, displayUnitParam, "km");
  };

  const formatDistance = (distance: number, storedUnit: DistanceUnit = "km"): string => {
    const converted = displayDistance(distance, storedUnit);
    return `${converted} ${distanceUnit}`;
  };

  const formatPace = (paceSeconds: number): string => {
    const mins = Math.floor(paceSeconds / 60);
    const secs = Math.round(paceSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculatePace = (distanceKm: number, durationSeconds: number): number => {
    if (distanceKm <= 0 || durationSeconds <= 0) return 0;
    const distanceInUserUnit = displayDistance(distanceKm, "km");
    return durationSeconds / distanceInUserUnit;
  };

  // Round barbell weight to 0.5 increments for cleaner display
  const displayBarbellWeight = Math.round(displayWeight(barbellWeightKg, "kg") * 2) / 2;
  
  // Convert stored kg increments to display units with higher precision
  const barbellIncrement: number | null = barbellIncrementKg !== null ? convertIncrementWeight(barbellIncrementKg, "kg", weightUnit) : null;
  const dumbbellIncrement: number | null = dumbbellIncrementKg !== null ? convertIncrementWeight(dumbbellIncrementKg, "kg", weightUnit) : null;
  const maxDumbbellWeight: number | null = maxDumbbellWeightKg !== null ? displayWeight(maxDumbbellWeightKg, "kg") : null;

  const barbellToTotalWeight = (enteredWeight: number, entryMode: BarbellEntryMode): number => {
    switch (entryMode) {
      case 'total':
        return enteredWeight;
      case 'total_added':
        return enteredWeight + barbellWeightKg;
      case 'per_side':
        return (enteredWeight * 2) + barbellWeightKg;
      default:
        return enteredWeight;
    }
  };

  const totalWeightToBarbell = (totalWeight: number, entryMode: BarbellEntryMode): number => {
    switch (entryMode) {
      case 'total':
        return totalWeight;
      case 'total_added':
        return Math.max(0, totalWeight - barbellWeightKg);
      case 'per_side':
        return Math.max(0, (totalWeight - barbellWeightKg) / 2);
      default:
        return totalWeight;
    }
  };

  const barbellToDumbbellWeight = (barbellTotalWeight: number): number => {
    return Math.round((barbellTotalWeight / 2) * 10) / 10;
  };

  const dumbbellToBarbellWeight = (dumbbellPerSide: number): number => {
    return dumbbellPerSide * 2;
  };

  const getSuggestedDumbbellWeight = (barbellWorkingWeight: number, entryMode: BarbellEntryMode): number => {
    const totalWeight = barbellToTotalWeight(barbellWorkingWeight, entryMode);
    return barbellToDumbbellWeight(totalWeight);
  };

  const updateBarbellWeight = (displayValue: number) => {
    const storageValue = toStorageWeight(displayValue, weightUnit);
    updatePreferences.mutate({ barbellWeight: storageValue });
  };

  return {
    weightUnit,
    distanceUnit,
    musicGenre,
    avatarId,
    experienceLevel,
    barbellWeight: displayBarbellWeight,
    barbellWeightKg,
    barbellEntryMode,
    barbellIncrement,
    dumbbellIncrement,
    maxDumbbellWeight,
    maxDumbbellWeightKg,
    maxDumbbellMode,
    progressionFrequency,
    progressionMode,
    rirEnabled,
    rirProgressThreshold,
    rirDeloadThreshold,
    workoutReminderTime,
    updatePreferences: updatePreferences.mutate,
    updateBarbellWeight,
    isUpdating: updatePreferences.isPending,
    clearUserData: clearUserData.mutate,
    isClearing: clearUserData.isPending,
    convertWeight,
    displayWeight,
    toStorageWeight,
    toStorageIncrement,
    formatWeight,
    convertDistance,
    displayDistance,
    toStorageDistance,
    formatDistance,
    formatPace,
    calculatePace,
    barbellToTotalWeight,
    totalWeightToBarbell,
    barbellToDumbbellWeight,
    dumbbellToBarbellWeight,
    getSuggestedDumbbellWeight,
    MUSIC_GENRES,
    AVATAR_OPTIONS,
    EXPERIENCE_LEVELS,
    BARBELL_PRESETS,
    BARBELL_ENTRY_MODES,
    PROGRESSION_FREQUENCIES,
    MAX_DUMBBELL_MODES,
  };
}
